import { FC, ReactNode } from 'react';

import { BoxProps, FormLabel, Typography, TypographyProps, useTheme } from '@mui/material';

import { Flex } from 'src/components/Flex';

import NPICurrencyInputField from './NPICurrencyInputField';

export enum CurrencyInputFieldLabelledLayout {
    HORIZONTAL = 'horizontal',
    VERTICAL = 'vertical',
}

interface CurrencyInputFieldLabelledProps {
    fieldName: string;
    header?: string | ReactNode;
    isDisabled?: boolean;
    layout?: CurrencyInputFieldLabelledLayout;
    textStyle?: TypographyProps;
    containerStyle?: BoxProps;
    renderHeader?: (header: string) => ReactNode;

    errorMessage?: string;
    dimensions?: {
        width?: string;
        height?: string;
        borderRadius?: string;
    };
    defaultValueInCents?: number;
    error?: boolean;
    setFieldValue?: (field: string, value: any) => void;
}

export const NPICurrencyInputFieldLabelled: FC<CurrencyInputFieldLabelledProps> = ({
    fieldName,
    header,
    isDisabled,
    layout = CurrencyInputFieldLabelledLayout.HORIZONTAL,
    textStyle,
    containerStyle,
    renderHeader,
    errorMessage,
    dimensions,
    ...inputProps
}) => {
    const theme = useTheme();

    const defaultHeaderRenderer = (headerText: string) => {
        return layout === CurrencyInputFieldLabelledLayout.VERTICAL ? (
            <FormLabel
                data-testid="vertical-form-label"
                sx={{ marginBottom: '8px', color: 'grey.900', fontWeight: 600 }}
            >
                {headerText}
            </FormLabel>
        ) : (
            <Typography
                data-testid="horizontal-form-label"
                fontSize="16px"
                fontWeight={400}
                color={theme.palette.grey.primary}
                lineHeight="24px"
                sx={{ maxLines: 1 }}
                {...textStyle}
            >
                {headerText}
            </Typography>
        );
    };

    return (
        <Flex
            justifyContent={
                layout === CurrencyInputFieldLabelledLayout.HORIZONTAL
                    ? 'space-between'
                    : 'flex-start'
            }
            flexDirection={
                layout === CurrencyInputFieldLabelledLayout.HORIZONTAL ? 'row' : 'column'
            }
            alignItems={
                layout === CurrencyInputFieldLabelledLayout.HORIZONTAL ? 'center' : 'flex-start'
            }
            gap={layout === CurrencyInputFieldLabelledLayout.HORIZONTAL ? '16px' : '8px'}
            {...containerStyle}
        >
            {header &&
                (renderHeader
                    ? renderHeader(typeof header === 'string' ? header : '')
                    : typeof header === 'string'
                    ? defaultHeaderRenderer(header)
                    : header)}
            <NPICurrencyInputField
                fieldName={fieldName}
                isDisabled={isDisabled}
                errorMessage={errorMessage}
                dimensions={{
                    width:
                        layout === CurrencyInputFieldLabelledLayout.HORIZONTAL ? '100px' : '100%',
                    height: '40px',
                    borderRadius: '4px',
                    ...dimensions,
                }}
                {...inputProps}
                header={undefined}
            />
        </Flex>
    );
};
